import {useMemo} from 'react'
import {useSelector} from 'react-redux'
import {
  getAnonymousId,
  getCurrencyCode,
  getDeviceId,
  getLocaleCode,
  getUserCountryCode
} from 'modules/meta/selectors'
import {
  getAnchorHotelCountry,
  getAppLockedDealConfig,
  getFirstHotelCountry,
  getIsAnchorHotel,
  getNumberOfRooms,
  getOfferAccessFlowSettings,
  getSearchId,
  getSearchParameters
} from 'modules/sapiSearch/selectors'
import {SearchOffer} from 'modules/sapiSearch/slice'
import {
  getClickId,
  getDealFreezeFilter,
  getFreeCancellation,
  getIsLandingItinerary,
  getIsOptimizeRooms,
  getIsStickyOtaMode,
  getOfferModeOverride,
  getPersistedUTMParams,
  getProfileUrlParam,
  getRateCheckerParams,
  getSearchHasBookingHint,
  getUrlParams
} from 'modules/search/selectors'
import {SearchUrlParams} from 'modules/search/types'
import {curry, identity, omit, pipe} from 'ramda'
import {ProviderRedirectQueryParamsType} from 'types/ProviderRedirect'
import {
  augmentBookURIWithAmenities,
  augmentBookURIWithClickId,
  augmentBookURIWithCurrency,
  augmentBookURIWithDealFreeze,
  augmentBookURIWithDeviceId,
  augmentBookURIWithFreeCancellation,
  augmentBookURIWithHasBookingHint,
  augmentBookURIWithHotelCountry,
  augmentBookURIWithIsAnchorHotel,
  augmentBookURIWithLang,
  augmentBookURIWithOptimizeRooms,
  augmentBookURIWithOta,
  augmentBookURIWithPlusTokenParam,
  augmentBookURIWithProfileParam,
  augmentBookURIWithRateCheckerParams,
  augmentBookURIWithRoomFilters,
  augmentBookURIWithSearchParameters,
  augmentBookURIWithSignUpTokenParam,
  augmentBookURIWithSRPSearchFlow,
  augmentBookURIWithSRPSearchId,
  augmentBookUriWithUserCountry,
  augmentBookURIWithUtmParams,
  generateRedirectLink,
  getBookUriProfile,
  getBookURISearchParameters
} from 'utils/offers'

import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {decorateUrlWithSubBrand} from '@vio/core/src/_web/brand/utils'
import {Page} from '@vio/core/src/analytics/types/Events'
import {useIsAudience} from '@vio/core/src/auth/hooks/useIsAudience'
import {getIsPrivateDeal} from '@vio/core/src/auth/modules/selectors'
import getIsBofhOffer from '@vio/core/src/offer/business/isBofhOffer'
import {
  augmentBookUriWithAnonymousId,
  hasOfferTag
} from '@vio/core/src/offer/business/offers'
import {hasUnlockPlusTokenInUrl} from '@vio/core/src/offer/business/privateDeals'
import {OfferTagLabel} from '@vio/core/src/offer/types/offer'
import {isRateChecker} from '@vio/core/src/rateChecker'
import {mapSearchToRoomsFilters} from '@vio/core/src/room/business/utils/filters'

import useGenerateAppDownloadUrl from './useGenerateAppDownloadUrl'
import {useGetSRPSearchFlow} from './useGetSRPSearchFlow'
import {useOtaMode} from './useOtaMode'

interface Props {
  anchorPriceTotal: number
  offer: SearchOffer
  hotelId?: string
}

// The pipe is pretty legible despite the amount of conditionals so the complexity limit is upped a bit
/* eslint complexity: ["error", 45] */
const useRedirectLink = (
  {anchorPriceTotal, offer, hotelId}: Props,
  ssrHydrated = true
): string => {
  const isAnchorHotel = useSelector(state => getIsAnchorHotel(state, hotelId))

  const {brand} = useBrand()
  const {hideInternalRedirect} = brand
  const localeCode = useSelector(getLocaleCode)
  const currencyCode = useSelector(getCurrencyCode)
  const anonymousId = useSelector(getAnonymousId)
  const utmParams = useSelector(getPersistedUTMParams)
  const urlParams = useSelector(getUrlParams) as SearchUrlParams
  const rateCheckerParams = useSelector(getRateCheckerParams)
  const profileUrlParam = useSelector(getProfileUrlParam)
  const isStickyOtaMode = useSelector(getIsStickyOtaMode)
  const offerModeOverride = useSelector(getOfferModeOverride)
  const {isOTAMode: isOta} = useOtaMode()
  const anchorHotelCountry = useSelector(getAnchorHotelCountry)
  const firstHotelCountry = useSelector(getFirstHotelCountry)
  const clickId = useSelector(getClickId)
  const hotelCountry = anchorHotelCountry || firstHotelCountry
  const userCountry = useSelector(getUserCountryCode)
  const isAudience = useIsAudience()
  const freeCancellation = useSelector(getFreeCancellation)
  const dealFreeze = useSelector(getDealFreezeFilter) // Added as a part of c747927f-deal-freeze-srp
  const {
    amenities = [],
    freeCancellation: freeCancellationParam,
    dealFreeze: dealFreezeParam
  } = urlParams || {}
  const activeRoomsFilters = mapSearchToRoomsFilters({
    freeCancellation: freeCancellationParam,
    amenities,
    dealFreeze: dealFreezeParam // Added as a part of c747927f-deal-freeze-srp
  })
  const searchParameters = useSelector(getSearchParameters)
  // The meta landing token only works for the hotel and dates the user landed on
  const canCarryLandingToken = useSelector(state =>
    getIsLandingItinerary(
      state,
      hotelId,
      searchParameters?.checkIn,
      searchParameters?.checkOut
    )
  )
  const isGems = useSelector(getIsPrivateDeal(offer))
  const isOptimizeRooms = useSelector(getIsOptimizeRooms)
  const numberOfRooms = useSelector(state => getNumberOfRooms(state, hotelId))
  const deviceId = useSelector(getDeviceId)
  const {appDownloadLinkId: placement} = useSelector(state =>
    getAppLockedDealConfig(state, hotelId, offer?.id)
  )

  const srpSearchFlow = useGetSRPSearchFlow()
  const hasBookingHint = useSelector(getSearchHasBookingHint)

  const {isAppLocked} = useSelector(state =>
    getOfferAccessFlowSettings(state, hotelId, offer?.id)
  )

  const isBofhOffer = useMemo(() => getIsBofhOffer(offer), [offer])
  const srpSearchId = useSelector(getSearchId)
  const totalPrice = offer?.totalRate

  const {generateAppLockedDealDeepLink} = useGenerateAppDownloadUrl(Page.Search)
  const appDownloadUrl = ssrHydrated
    ? generateAppLockedDealDeepLink(placement, {
        hotelId,
        clickedOfferRate: totalPrice,
        currency: currencyCode
      })
    : ''

  const shouldOverrideProfileParam =
    (utmParams.utm_source === 'gha' &&
      brand.shouldAugmentBookUriWithOtaModeProfile) ||
    isStickyOtaMode

  const overwrittenProfileUrlParam = getBookUriProfile(
    offerModeOverride,
    shouldOverrideProfileParam,
    profileUrlParam
  )

  if (!offer) return undefined

  if (isAppLocked) {
    return appDownloadUrl
  }

  const {currency, intermediaryProvider, url} = offer

  const shouldAddSignUpToken = isBofhOffer && isAudience

  const shouldAddPlusToken = isBofhOffer && hasUnlockPlusTokenInUrl(urlParams)
  const shouldForwardRateCheckerParams =
    isBofhOffer && isRateChecker(urlParams as Record<string, unknown>)

  const redirectURI = pipe<
    [string],
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string
  >(
    curry(augmentBookUriWithAnonymousId)(anonymousId),
    shouldAddSignUpToken ? augmentBookURIWithSignUpTokenParam : identity,
    shouldAddPlusToken ? augmentBookURIWithPlusTokenParam : identity,
    isBofhOffer && deviceId
      ? curry(augmentBookURIWithDeviceId)(deviceId)
      : identity,
    shouldForwardRateCheckerParams
      ? curry(augmentBookURIWithRateCheckerParams)(rateCheckerParams)
      : identity,
    isBofhOffer
      ? curry(augmentBookURIWithUtmParams)(
          omit(['label', 'vtm_src'], utmParams)
        )
      : identity,
    isBofhOffer
      ? curry(augmentBookURIWithHotelCountry)(hotelCountry)
      : identity,
    isBofhOffer ? curry(augmentBookUriWithUserCountry)(userCountry) : identity,
    isBofhOffer && clickId
      ? curry(augmentBookURIWithClickId)(clickId)
      : identity,
    isBofhOffer && isOta ? curry(augmentBookURIWithOta)(isOta) : identity,
    isBofhOffer && !isAnchorHotel
      ? curry(augmentBookURIWithFreeCancellation)(freeCancellation)
      : identity,
    isBofhOffer
      ? curry(augmentBookURIWithOptimizeRooms)(isOptimizeRooms)
      : identity,
    isBofhOffer && overwrittenProfileUrlParam
      ? curry(augmentBookURIWithProfileParam)(overwrittenProfileUrlParam)
      : identity,
    isBofhOffer && amenities.length && !isAnchorHotel
      ? curry(augmentBookURIWithAmenities)(amenities)
      : identity,
    isBofhOffer ? curry(augmentBookURIWithSRPSearchId)(srpSearchId) : identity,
    isBofhOffer
      ? curry(augmentBookURIWithIsAnchorHotel)(isAnchorHotel)
      : identity,
    isBofhOffer
      ? curry(augmentBookURIWithSearchParameters)(
          getBookURISearchParameters(searchParameters, canCarryLandingToken)
        )
      : identity,
    isBofhOffer
      ? curry(augmentBookURIWithHasBookingHint)(hasBookingHint)
      : identity,
    isBofhOffer ? curry(decorateUrlWithSubBrand) : identity,
    // Always add lang and currency to preserve them when navigating from Search WL to Vio
    localeCode ? curry(augmentBookURIWithLang)(localeCode) : identity,
    currencyCode ? curry(augmentBookURIWithCurrency)(currencyCode) : identity,
    isBofhOffer
      ? curry(augmentBookURIWithSRPSearchFlow)(srpSearchFlow)
      : identity,
    isBofhOffer && !isAnchorHotel
      ? curry(augmentBookURIWithRoomFilters)(activeRoomsFilters)
      : identity,
    isBofhOffer && !isAnchorHotel
      ? curry(augmentBookURIWithDealFreeze)(dealFreeze)
      : identity
  )(url)

  const redirectParams: ProviderRedirectQueryParamsType = {
    anchorPrice: anchorPriceTotal * numberOfRooms,
    currency,
    providerCode: intermediaryProvider,
    redirectURI,
    totalPrice: totalPrice,
    lang: localeCode,
    yourChoice: hasOfferTag(offer, OfferTagLabel.IsYourChoice),
    isGems,
    ...(offer.metadata.brandInfo && {
      brandInfo: encodeURIComponent(
        JSON.stringify({
          // Passing only required fields for ProviderLogo
          logoURL: offer.metadata.brandInfo.logoURL,
          iconURL: offer.metadata.brandInfo.iconURL
        })
      )
    })
  }

  const redirectLink = generateRedirectLink(
    redirectParams,
    hideInternalRedirect,
    isBofhOffer
  )

  return redirectLink
}

export default useRedirectLink
